import { useState, useEffect } from 'react';

const LOCATIONS_DATA_READY_EVENT = 'DDC:LocationsDataReady';
const TIMEOUT_MS = 5000;

/**
 * Hook to integrate with ws-locations-data widget.
 * Subscribes to the global window.DDC.LocationsData API and tracks
 * whether location data is available so facets can be shown/hidden.
 *
 * @returns {{ hasLocations: boolean, isReady: boolean }}
 */
export const useLocationsData = () => {
	const [isReady, setIsReady] = useState(false);
	const [hasLocations, setHasLocations] = useState(false);

	// Detect when LocationsData API becomes available
	useEffect(() => {
		if (typeof window === 'undefined') {
			return () => {};
		}

		if (window.DDC?.LocationsData) {
			setIsReady(true);
			return () => {};
		}

		const handleReady = () => {
			setIsReady(true);
		};

		window.addEventListener(LOCATIONS_DATA_READY_EVENT, handleReady);

		const timeout = setTimeout(() => {
			if (!window.DDC?.LocationsData) {
				setIsReady(false);
			}
		}, TIMEOUT_MS);

		return () => {
			window.removeEventListener(LOCATIONS_DATA_READY_EVENT, handleReady);
			clearTimeout(timeout);
		};
	}, []);

	// Subscribe to location updates to track hasLocations
	useEffect(() => {
		if (!isReady || !window.DDC?.LocationsData) {
			return () => {};
		}

		const handleLocationsUpdate = () => {
			const locationsData = window.DDC.LocationsData;
			const locations = locationsData.getLocations() || [];
			const error = locationsData.getError();
			setHasLocations(!error && locations.length > 0);
		};

		// Check current state immediately
		handleLocationsUpdate();

		const unsubscribe =
			window.DDC.LocationsData.registerLocationsNotification(
				handleLocationsUpdate
			);

		return () => {
			if (unsubscribe) {
				unsubscribe();
			}
		};
	}, [isReady]);

	return { hasLocations, isReady };
};
